import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Image from '../components/image/Image';
import { ITableData } from '../types/interfaces';
import { routes } from '../routes/routePaths';
import NewAssetsToIntegrateModal from '../components/modals/NewAssetsToIntegrateModal';
import { getAssetsToIntegrate } from '../services/GuaranteesService';

interface AssetToIntegrate {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const AssetsToIntegrate = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<any[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewAssetModalOpen, setIsNewAssetModalOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchAssets();
  }, [page]);

  const fetchAssets = async () => {
    setIsLoading(true);
    try {
      const response = await getAssetsToIntegrate();
      if (response.success && 'data' in response) {
        const assetsData: AssetToIntegrate[] = response.data;
        
        // Map assets to table format
        const mappedAssets = assetsData.map((asset) => ({
          id: asset.id,
          name: asset.name,
          description: asset.description,
        }));

        setAssets(mappedAssets);
        
        // Since the API doesn't return pagination info, we'll set it manually
        setTableData({
          size: mappedAssets.length,
          page: 0,
          totalElements: mappedAssets.length,
          totalPages: 1,
        });
      } else {
        setAssets([]);
      }
    } catch (error) {
      console.error('Error fetching assets to integrate:', error);
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleOpenNewAssetModal = () => {
    setIsNewAssetModalOpen(true);
  };

  const handleCloseNewAssetModal = () => {
    setIsNewAssetModalOpen(false);
  };

  const handleAssetCreated = () => {
    fetchAssets();
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (row: any) => row.name,
      sortable: false,
      minWidth: '300px',
    },
    {
      name: 'Descripción',
      selector: (row: any) => row.description,
      sortable: false,
      minWidth: '400px',
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      {/* Header */}
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '4' }}>
        <Card display='flex' alignItems='center' gap='16px'>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a configuración"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Activos a integrar
          </H>
        </Card>
        <Button variant='primary' onClick={handleOpenNewAssetModal}>
          Nuevo activo
        </Button>
      </Card>

      {/* Table or Empty State */}
      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : assets.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={assets}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay activos a integrar creados.
          </P>
          <P variant='primary' align='center'>
            Podés crear uno nuevo.
          </P>
          <Button variant='primary' onClick={handleOpenNewAssetModal}>
            Nuevo activo
          </Button>
        </Card>
      )}

      {/* New Asset Modal */}
      <NewAssetsToIntegrateModal
        isOpen={isNewAssetModalOpen}
        onClose={handleCloseNewAssetModal}
        onSuccess={handleAssetCreated}
      />
    </Card>
  );
};

export default AssetsToIntegrate;
